import LoadingButton from '@mui/lab/LoadingButton';
import { HydratedCorpJobJsonWithOutput } from '@scale/shared/types/corpJobs/HydratedCorpJobJson';
import React from 'react';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import { RemoAdminLookupLink } from 'react-scale/components/remoAdmin/remoAdminLookupLink';
import apiFetch, { defaultApiFetchErrorHandler } from 'react-scale/lib/api-fetch';
import { invokeAsyncCorpJob } from 'react-scale/lib/invokeCorpJob';
import Link from 'corp/components/UI/IsomorphicLink/IsomorphicLink';
import { useAsync } from 'react-use-v13';

interface IAuditPlusFixButtonProps {
  auditPlusIds?: string[];
  projectId: string;
}

export const AuditPlusFixButton: React.FC<IAuditPlusFixButtonProps> = ({
  projectId,
  auditPlusIds,
}) => {
  const { isRemoAdmin } = useRemoAdminCtx();
  const [running, setRunning] = React.useState<boolean>(false);
  const [job, setJob] = React.useState<HydratedCorpJobJsonWithOutput>();

  const { loading, value: pendingAuditPlusCount } = useAsync(async () => {
    try {
      const body = auditPlusIds ? { auditPlusIds } : undefined;
      const options = { method: 'POST', body };
      const { pendingAuditPlusCount } = await apiFetch(
        null,
        `/corp-api/quality/${projectId}/pending-auditplus`,
        options,
      );
      return pendingAuditPlusCount as number;
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
  }, [projectId, auditPlusIds]);

  const runCorpJob = React.useCallback(async () => {
    setRunning(true);
    try {
      const newJob = await invokeAsyncCorpJob('auditPlusCompletionRetry', {
        projectId,
        auditPlusIds,
      });
      setJob(newJob);
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
    setRunning(false);
  }, [projectId, auditPlusIds]);

  return (
    <>
      {!job && (
        <LoadingButton
          variant="outlined"
          loading={running || loading}
          disabled={job !== undefined || !pendingAuditPlusCount}
          onClick={runCorpJob}
        >
          Complete AuditPlus for {pendingAuditPlusCount} tasks.
        </LoadingButton>
      )}
      {job &&
        (isRemoAdmin ? (
          <RemoAdminLookupLink displayValue={`View your job's execution here.`} id={job._id} />
        ) : (
          <Link href={`/lookup/${job._id}`}>View your job's execution here.</Link>
        ))}
    </>
  );
};
